import {
  BaseSyntheticEvent,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  FieldPath,
  FormProvider,
  SubmitErrorHandler,
  SubmitHandler,
  useForm,
  useWatch,
} from 'react-hook-form';

import { Accordion } from '@radix-ui/react-accordion';
import { get, set } from 'lodash';
import {
  Building2,
  CircleDollarSignIcon,
  InfoIcon,
  MapPinnedIcon,
  PackageIcon,
  ReceiptIcon,
  StickyNote,
  TagsIcon,
} from 'lucide-react';
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore posthog is in the parent dir
import { usePostHog } from 'posthog-js/react';

import { Button } from 'components/Button';
import LoadContextProvider from 'components/LoadContextProvider';
import { Flex } from 'components/layout';
import ButtonLoader from 'components/loading/ButtonLoader';
import { Typography } from 'components/typography';
import { ExtendedFormProvider } from 'contexts/extendedFormContext';
import { SidebarStateContext } from 'contexts/sidebarStateContext';
import { useFetchEquipmentTypes } from 'hooks/useFetchEquipmentTypes';
import { useFetchMGBAccessorialCharges } from 'hooks/useFetchMGBAccessorialCharges';
import { useFetchReferenceTypes } from 'hooks/useFetchReferenceTypes';
import { useFetchServiceTypes } from 'hooks/useFetchServiceTypes';
import { useFetchSpecialReferences } from 'hooks/useFetchSpecialReferences';
import { useGuardedCreateLoadMercuryGateBoomi } from 'hooks/useGuardedCreateLoadMercuryGateBoomi';
import { useServiceFeatures } from 'hooks/useServiceContext';
import useTMSContext from 'hooks/useTMSContext';
import { useToast } from 'hooks/useToaster';
import { cancelLoad } from 'lib/api/cancelLoad';
import { getCustomers } from 'lib/api/getCustomers';
import { searchCustomers } from 'lib/api/searchCustomers';
import { updateTMS } from 'lib/api/updateTMS';
import { FormStorageService } from 'lib/services/FormStorage/service';
import { LoadSectionAccordionItem } from 'pages/LoadView/LoadInformation/Components';
import DuplicateLoadWarningDialog from 'pages/QuoteView/LoadBuilding/DuplicateLoadWarningDialog';
import LoadBuildingAccordionControls from 'pages/QuoteView/LoadBuilding/components/LoadBuildingAccordionControls';
import { LoadCreatedMessage } from 'pages/QuoteView/LoadBuilding/components/LoadCreatedMessage';
import { useLoadBuildingAccordionTabs } from 'pages/QuoteView/LoadBuilding/hooks/useLoadBuildingAccordionTabs';
import { QuoteMode } from 'pages/QuoteView/Quoting/RequestQuickQuote/types';
import {
  Load,
  NormalizedLoad,
  TMSCustomer,
  createInitLoad,
  normalizeLoad,
} from 'types/Load';
import { LoadAttributes } from 'types/LoadAttributes';
import { Maybe, Undef } from 'types/UtilityTypes';
import { TMSOperation } from 'types/api/UpdateTMS';
import ButtonText from 'types/enums/ButtonText';
import { ButtonNamePosthog } from 'types/enums/CustomPosthogEvents';
import { TMS } from 'types/enums/Integrations';
import { SuggestionPipelines } from 'types/suggestions/CoreSuggestions';
import {
  LoadBuildingSuggestions,
  SuggestedLoad,
} from 'types/suggestions/LoadBuildingSuggestions';
import {
  acceptLoadViewSuggestion,
  useApplyLoadViewSuggestion,
  useFocusLoadViewSuggestion,
} from 'utils/applyLoadViewSuggestion';
import { applyCompleteKitSuggestions } from 'utils/completeKitSuggestion';
import {
  DEFAULT_MAX_VALIDATION_TOAST_LABELS,
  type ManualFieldError,
  applyManualValidationErrors,
  applySuggestionHydrationValidationErrors,
  formatFormErrors,
  handleFormErrors,
  loadBuildingManualValidationDisplayOptions,
  scheduleLoadBuildingSuggestionValidation,
} from 'utils/formValidation';
import {
  injectSelectedObject,
  withLoadBuildingSuggestionContext,
} from 'utils/loadInfoAndBuilding';

import AdditionalReferencesForm from './AdditionalReferences';
import BillToSectionForm from './BillTo';
import CustomerSectionForm from './Customer';
import NotesForm from './Notes';
import RatesForm from './Rates';
import RouteStops from './RouteStops';
import SpecificationsForm from './Specifications';
import {
  COLLECTION_METHOD_REQUIRED_ERROR,
  applyMGBWallClockDatesForWire,
  buildAccessorials,
  buildAdditionalReferences,
  buildCreateLoadRequest,
  buildLoadCommodities,
  buildNotes,
  buildRateData,
  buildWireStops,
  mergeMGBSpecificationsForUpdate,
  prepareMGBValuesForWire,
} from './buildRequestBody';
import MGBCancelLoadDialog from './components/MGBCancelLoadDialog';
import {
  MGB_CARRIER_REP_CODE_TABLE,
  MGB_CUSTOMER_REP_CODE_TABLE,
  MGB_QUOTE_NUMBER_REF_TYPE,
  MGB_RATES_ACCESSORIALS_SECTION_ID,
  MGB_TRAILER_LENGTH_CODE_TABLE,
} from './constants';
import { withResolvedMGBStopCountry } from './mgbGeography';
import {
  hydrateMGBCommodityStopFieldsFromForm,
  mgbActiveStopTabFromErrors,
} from './mgbStops';
import {
  validateMGBAdditionalReferencesForSubmit,
  validateMGBCommoditiesForSubmit,
  validateMGBMultiStopsForSubmit,
  validateMGBRatesForSubmit,
  validateMGBStopStatesForSubmit,
} from './mgbValidation';
import { useMGBCustomerHydration } from './useMGBCustomerHydration';
import {
  KENCO_MGB_SUGGESTION_MAPS,
  MGB_EDI_CUSTOMER_BLOCK_MESSAGE,
  buildMGBFormFromSuggestion,
  canonicalizeSpecialReferenceCodes,
  filterMGBCreateCustomers,
  filterMGBCustomers,
  getAdditionalReferenceNumber,
  initMGBLoadForm,
  injectCompanyIfMissing,
  isMGBEDICustomerSelected,
  isMGBLoadCancelled,
  isSystemQuoteNumberRef,
  mergeMGBCustomersByExternalID,
  parseDrumkitQuoteNumber,
  resolveMGBCustomerNameForOptions,
  resolveMGBQuoteRequestIdForCreate,
  resolveMGBSelectedCustomer,
  upsertTMSCustomer,
  withMGBFormDefaults,
} from './utils';

enum AvailableTabs {
  customer = 'customer',
  billTo = 'billTo',
  commodities = 'commodities',
  references = 'references',
  rateData = 'rateData',
  route = 'route',
  pickup = 'pickup',
  consignee = 'consignee',
  accessorials = 'accessorials',
  notes = 'notes',
}

interface UnifiedMercuryGateBoomiLoadFormProps {
  isCreateMode?: boolean;
  load?: Load;
  onSuccess?: (result: {
    load?: Load;
    loadAttributes?: LoadAttributes;
    message?: string;
  }) => void;
  onError?: (error: {
    load?: Load;
    loadAttributes?: LoadAttributes;
    message?: string;
  }) => void;
}

/** Prefer LD/BOL (customer.refNumber); fall back to freight then external TMS ID. */
function resolveCreatedLoadDisplayId(load: Maybe<Load>): string | undefined {
  const bol = load?.customer?.refNumber?.trim();
  if (bol) {
    return bol;
  }
  const freightId = load?.freightTrackingID?.trim();
  if (freightId) {
    return freightId;
  }
  const externalId = load?.externalTMSID?.trim();

  return externalId || undefined;
}

function resolveCreatedLoadDisplayLabel(load: Maybe<Load>): string {
  return load?.customer?.refNumber?.trim() ? 'BOL' : 'Shipment ID';
}

function resolveQuoteNumberFromAdditionalReferences(
  load: Maybe<Load>
): string | undefined {
  const refs = load?.additionalReferences ?? [];
  const match = refs.find((ref) => isSystemQuoteNumberRef(ref.qualifier));
  const number = match?.number?.trim();

  return number || undefined;
}

function resolveCreateSuccessDescription(
  load: Maybe<Load>
): string | undefined {
  const parts: string[] = [];
  const displayId = resolveCreatedLoadDisplayId(load);
  if (displayId) {
    parts.push(`${resolveCreatedLoadDisplayLabel(load)}: ${displayId}`);
  }
  const quoteNumber = resolveQuoteNumberFromAdditionalReferences(load);
  if (quoteNumber) {
    parts.push(`Quote Number: ${quoteNumber}`);
  }

  return parts.length > 0 ? parts.join('\n') : undefined;
}

function flattenFormValues(
  values: Record<string, unknown>,
  parentKey = '',
  result: string[] = []
): string[] {
  for (const key in values) {
    const value = values[key];
    const newKey = parentKey ? `${parentKey}.${key}` : key;

    if (value && typeof value === 'object' && !Array.isArray(value)) {
      flattenFormValues(value as Record<string, unknown>, newKey, result);
    } else {
      result.push(newKey);
    }
  }

  return result;
}

export default function UnifiedMercuryGateBoomiLoadForm({
  isCreateMode = true,
  load,
  onSuccess,
  onError,
}: UnifiedMercuryGateBoomiLoadFormProps) {
  const tmsName = TMS.MercurygateBoomi;
  const { tmsID, isTestingEnvironment } = useTMSContext();
  const { serviceID } = useServiceFeatures();
  const { toast } = useToast();
  const {
    currentState: {
      clickedSuggestion,
      threadId,
      emailId,
      threadItemId,
      goToSuggestion,
    },
    setCurrentState,
  } = useContext(SidebarStateContext);

  const [customers, setCustomers] = useState<Maybe<TMSCustomer[]>>(null);
  const [customersByExternalID, setCustomersByExternalID] = useState<
    Record<string, TMSCustomer>
  >({});
  // Keep a live ref so successive setVisibleCustomers calls merge into the
  // latest lookup (state from the previous call may not have re-rendered yet).
  const customersByExternalIDRef = useRef(customersByExternalID);
  customersByExternalIDRef.current = customersByExternalID;
  const retainedCreateCustomerIdRef = useRef<string>('');
  const guardedCreateLoad = useGuardedCreateLoadMercuryGateBoomi();
  const posthog = usePostHog();
  const [isCustomersLoading, setIsCustomersLoading] = useState(false);
  const [showAIHints, setShowAIHints] = useState(false);

  const { referenceTypes, isLoading: isReferenceTypesLoading } =
    useFetchReferenceTypes(tmsID);

  // Accordion order: Customer → Route → Specs → Rates → References → Notes
  // so stops are entered before rate / mileage entry.
  const allTabs = useMemo(() => {
    const tabs = [
      AvailableTabs.customer,
      AvailableTabs.route,
      AvailableTabs.pickup,
      AvailableTabs.consignee,
      AvailableTabs.commodities,
      AvailableTabs.rateData,
      AvailableTabs.references,
      AvailableTabs.notes,
    ];
    if (!isCreateMode) {
      tabs.splice(1, 0, AvailableTabs.billTo);
    }

    return tabs;
  }, [isCreateMode]);

  // Top-level headers only — nested pickup/consignee can stay in activeTabs after Route closes.
  const sectionTabs = useMemo(
    () =>
      allTabs.filter(
        (tab) => tab !== AvailableTabs.pickup && tab !== AvailableTabs.consignee
      ),
    [allTabs]
  );

  const {
    activeTabs,
    setActiveTabs,
    expandAll,
    collapseAll,
    defaultView,
    setDefaultView,
  } = useLoadBuildingAccordionTabs({ allTabs });

  const handleAccordionValueChange = useCallback(
    (next: string[]) => {
      let tabs = next;
      if (!tabs.includes(AvailableTabs.route)) {
        tabs = tabs.filter(
          (tab) =>
            tab !== AvailableTabs.pickup && tab !== AvailableTabs.consignee
        );
      }
      setActiveTabs(tabs);
    },
    [setActiveTabs]
  );
  const [isSubmitting, setIsSubmitting] = useState(false);
  const submitInFlightRef = useRef(false);
  const [createSucceeded, setCreateSucceeded] = useState(false);
  const [createdLoad, setCreatedLoad] = useState<Maybe<Load>>(null);
  const [showDuplicateWarningDialog, setShowDuplicateWarningDialog] =
    useState(false);
  const [pendingSubmissionData, setPendingSubmissionData] =
    useState<Undef<NormalizedLoad>>(undefined);
  const [warningMessage, setWarningMessage] = useState<string | undefined>(
    undefined
  );
  const [isCancelDialogOpen, setIsCancelDialogOpen] = useState(false);
  const [isCancellingLoad, setIsCancellingLoad] = useState(false);
  const [loadIsCancelled, setLoadIsCancelled] = useState(() =>
    !isCreateMode && load ? isMGBLoadCancelled(load) : false
  );

  const prevCustomerExternalTMSIDRef = useRef<string | undefined>(undefined);
  const lastFormDefaultsSnapshotRef = useRef<string | null>(null);
  const createSessionSuggestionRef = useRef<Maybe<{ id: number }>>(null);
  const persistCreateDraftsRef = useRef(true);

  const skipFormDefaultsResetRef = useRef(false);
  const draftRestoreSuggestionIdRef = useRef<Undef<number | null>>(undefined);
  const pendingEquipmentSettledRevalidationSuggestionIdRef = useRef<
    number | null
  >(null);
  const equipmentSettledRevalidationSuggestionIdRef = useRef<
    number | null | undefined
  >(undefined);
  const runSuggestionHydrationValidationRef = useRef<
    () => void | Promise<void>
  >(() => undefined);

  const equipmentScopedCustomerIdRef = useRef<string | null>(null);
  const [equipmentScopedCustomerId, setEquipmentScopedCustomerId] = useState<
    string | null
  >(null);

  const alignEquipmentScopedCustomerId = (customerExternalId: string) => {
    equipmentScopedCustomerIdRef.current = customerExternalId;
    setEquipmentScopedCustomerId(customerExternalId);
  };
  const [customerRepClearToken, setCustomerRepClearToken] = useState(0);
  const [activeRouteStopTab, setActiveRouteStopTab] = useState<
    string | undefined
  >();
  const [routeStopFocusKey, setRouteStopFocusKey] = useState(0);

  const handleActiveRouteStopTabHandled = useCallback(() => {
    setActiveRouteStopTab(undefined);
  }, []);

  const jumpToRatesAccessorials = useCallback(() => {
    setActiveTabs((prev) =>
      prev.includes(AvailableTabs.rateData)
        ? prev
        : [...prev, AvailableTabs.rateData]
    );

    window.setTimeout(() => {
      document
        .getElementById(MGB_RATES_ACCESSORIALS_SECTION_ID)
        ?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 50);
  }, [setActiveTabs]);

  const expandSpecsForUnresolvedEquipment = useCallback(() => {
    setActiveTabs((prev) =>
      prev.includes(AvailableTabs.commodities)
        ? prev
        : [...prev, AvailableTabs.commodities]
    );
  }, [setActiveTabs]);

  const focusRouteStopFromValidationErrors = useCallback(
    (errors: ReadonlyArray<{ path: string }>) => {
      const stopTab = mgbActiveStopTabFromErrors(errors);
      if (stopTab) {
        setActiveRouteStopTab(stopTab);
        setRouteStopFocusKey((key) => key + 1);
      }
    },
    []
  );

  const createModeLbSuggestion =
    isCreateMode &&
    clickedSuggestion?.pipeline === SuggestionPipelines.LoadBuilding
      ? (clickedSuggestion as LoadBuildingSuggestions)
      : null;

  const createModeLoadBuildingSuggestionId = createModeLbSuggestion?.id ?? null;

  const clickedSuggestionRef = useRef(clickedSuggestion);
  clickedSuggestionRef.current = clickedSuggestion;

  const defaultValues = useMemo(() => {
    if (!isCreateMode && load) {
      return withMGBFormDefaults(normalizeLoad(tmsName, load));
    }

    if (isCreateMode && createSucceeded && createdLoad) {
      return withMGBFormDefaults(normalizeLoad(tmsName, createdLoad));
    }

    if (createModeLoadBuildingSuggestionId != null) {
      const suggestion =
        clickedSuggestionRef.current as LoadBuildingSuggestions;

      return buildMGBFormFromSuggestion(
        suggestion.suggested as SuggestedLoad,
        tmsName,
        {
          drumkitQuoteNumber: suggestion.drumkitQuoteNumber,
          quoteRequestId: suggestion.quoteRequestId,
          lowBuyRate: suggestion.lowBuyRate,
          highBuyRate: suggestion.highBuyRate,
        }
      );
    }

    return initMGBLoadForm(tmsName);
  }, [
    isCreateMode,
    load,
    tmsName,
    createSucceeded,
    createdLoad,
    createModeLoadBuildingSuggestionId,
  ]);

  const formMethods = useForm<NormalizedLoad>({
    defaultValues,
    mode: 'onSubmit',
  });

  const { control, reset, setValue, getValues, clearErrors, setError, watch } =
    formMethods;

  const defaultValuesRef = useRef(defaultValues);
  defaultValuesRef.current = defaultValues;
  const goToSuggestionRef = useRef(goToSuggestion);
  goToSuggestionRef.current = goToSuggestion;
  const createModeLbSuggestionRef = useRef(createModeLbSuggestion);
  createModeLbSuggestionRef.current = createModeLbSuggestion;
  const draftRestoredForThreadRef = useRef<string | null>(null);
  const activeLoadBuildingSuggestionId =
    clickedSuggestion?.pipeline === SuggestionPipelines.LoadBuilding
      ? clickedSuggestion.id
      : null;

  const activeTabsRef = useRef(activeTabs);
  activeTabsRef.current = activeTabs;

  const hydrationManualPathsRef = useRef<string[]>([]);
  const submitManualPathsRef = useRef<string[]>([]);

  const collectMGBManualHydrationErrors =
    useCallback((): ManualFieldError<NormalizedLoad>[] => {
      const formValues = getValues();
      const hydratedCommodities = hydrateMGBCommodityStopFieldsFromForm(
        getValues,
        formValues.commodities
      );
      const values: NormalizedLoad = {
        ...formValues,
        commodities: hydratedCommodities,
      };
      const preparedForWire = prepareMGBValuesForWire(values);

      return [
        ...validateMGBCommoditiesForSubmit(values.commodities),
        ...validateMGBAdditionalReferencesForSubmit(
          values.additionalReferences
        ),
        ...validateMGBStopStatesForSubmit(values),
        ...validateMGBMultiStopsForSubmit(preparedForWire),
        ...validateMGBRatesForSubmit(values.rateData),
      ];
    }, [getValues]);

  const runSuggestionHydrationValidation = useCallback(async () => {
    await formMethods.trigger();

    const manualErrors = collectMGBManualHydrationErrors();
    hydrationManualPathsRef.current = manualErrors.map((error) =>
      String(error.path)
    );

    const rhfErrors = formMethods.getErrors();
    focusRouteStopFromValidationErrors([
      ...formatFormErrors(rhfErrors),
      ...manualErrors,
    ]);
    applySuggestionHydrationValidationErrors(formMethods, manualErrors, {
      tmsName: TMS.MercurygateBoomi,
      activeTabs: activeTabsRef.current,
      setActiveTabs,
    });
  }, [
    formMethods,
    collectMGBManualHydrationErrors,
    focusRouteStopFromValidationErrors,
    setActiveTabs,
  ]);
  runSuggestionHydrationValidationRef.current =
    runSuggestionHydrationValidation;

  useEffect(() => {
    if (createSucceeded) {
      setShowAIHints(false);
    } else if (
      isCreateMode &&
      clickedSuggestion?.pipeline === SuggestionPipelines.LoadBuilding
    ) {
      setShowAIHints(true);
    } else {
      setShowAIHints(false);
    }
  }, [
    isCreateMode,
    clickedSuggestion?.id,
    clickedSuggestion?.pipeline,
    createSucceeded,
  ]);

  // Reset when normalized form defaults change (edit refresh or create suggestion switch),
  // but skip when the parent only passes a new object reference with the same data.
  useEffect(() => {
    const snapshot = JSON.stringify(defaultValues);
    const shouldValidate =
      createModeLoadBuildingSuggestionId != null && !createSucceeded;

    if (lastFormDefaultsSnapshotRef.current === snapshot) {
      return scheduleLoadBuildingSuggestionValidation(
        shouldValidate,
        runSuggestionHydrationValidation
      );
    }

    if (skipFormDefaultsResetRef.current) {
      // Absorb the new defaults baseline without wiping the restored draft.
      lastFormDefaultsSnapshotRef.current = snapshot;
      const expectedSuggestionId = draftRestoreSuggestionIdRef.current;
      const activeLbSuggestion = createModeLbSuggestionRef.current;
      if (
        expectedSuggestionId != null &&
        activeLbSuggestion?.id === expectedSuggestionId
      ) {
        skipFormDefaultsResetRef.current = false;
        draftRestoreSuggestionIdRef.current = undefined;

        return;
      }
      if (
        expectedSuggestionId != null &&
        activeLbSuggestion != null &&
        activeLbSuggestion.id !== expectedSuggestionId
      ) {
        // User picked a different suggestion — stop suppressing and apply it.
        skipFormDefaultsResetRef.current = false;
        draftRestoreSuggestionIdRef.current = undefined;
      } else {
        return;
      }
    }

    lastFormDefaultsSnapshotRef.current = snapshot;

    reset(defaultValues, {
      keepDefaultValues: false,
      keepDirty: false,
      keepTouched: false,
    });

    prevCustomerExternalTMSIDRef.current =
      defaultValues.customer?.externalTMSID?.trim() ?? undefined;
    alignEquipmentScopedCustomerId(
      defaultValues.customer?.externalTMSID?.trim() ?? ''
    );

    if (!shouldValidate) {
      hydrationManualPathsRef.current = [];
    }

    // After AI suggestion hydrate, run the same FE validators as submit
    // so missing required fields (including empty reference type) are visible
    // without clicking Create. Soft treatment: field errors + accordion expand,
    // no destructive toast / auto-scroll.
    return scheduleLoadBuildingSuggestionValidation(
      shouldValidate,
      runSuggestionHydrationValidation
    );
  }, [
    defaultValues,
    reset,
    formMethods,
    createModeLoadBuildingSuggestionId,
    createSucceeded,
    runSuggestionHydrationValidation,
  ]);

  useEffect(() => {
    if (createModeLoadBuildingSuggestionId == null || createSucceeded) {
      hydrationManualPathsRef.current = [];
      return undefined;
    }

    const subscription = watch(() => {
      const tracked = hydrationManualPathsRef.current;
      if (tracked.length === 0) {
        return;
      }

      const stillFailingErrors = collectMGBManualHydrationErrors();
      const stillFailing = new Set(
        stillFailingErrors.map((error) => String(error.path))
      );
      const resolved = tracked.filter((path) => !stillFailing.has(path));
      if (resolved.length === 0) {
        return;
      }

      clearErrors(resolved as never);

      const wipedNested = stillFailingErrors.filter((error) => {
        const errorPath = String(error.path);
        return resolved.some(
          (cleared) =>
            errorPath !== cleared && errorPath.startsWith(`${cleared}.`)
        );
      });
      for (const { path, message } of wipedNested) {
        setError(path, { type: 'manual', message });
      }

      hydrationManualPathsRef.current = stillFailingErrors.map((error) =>
        String(error.path)
      );
    });

    return () => {
      subscription.unsubscribe();
    };
  }, [
    createModeLoadBuildingSuggestionId,
    createSucceeded,
    watch,
    clearErrors,
    setError,
    collectMGBManualHydrationErrors,
  ]);

  useApplyLoadViewSuggestion(
    setValue,
    clickedSuggestion,
    load?.freightTrackingID,
    !isCreateMode,
    defaultValues
  );

  useFocusLoadViewSuggestion(
    clickedSuggestion,
    load?.freightTrackingID,
    setActiveTabs,
    !isCreateMode,
    Object.values(AvailableTabs)
  );

  useEffect(() => {
    if (!isCreateMode && load) {
      setLoadIsCancelled(isMGBLoadCancelled(load));
    }
  }, [isCreateMode, load]);

  const watchedCustomer = useWatch({ control, name: 'customer' });
  const watchedCustomerExternalTMSID = watchedCustomer?.externalTMSID;
  const watchedCustomerName = watchedCustomer?.name?.trim() ?? '';
  retainedCreateCustomerIdRef.current =
    watchedCustomerExternalTMSID?.trim() ?? '';
  const selectedCustomer = useMemo(
    () =>
      resolveMGBSelectedCustomer(
        customersByExternalID,
        customers,
        watchedCustomer
      ),
    [customersByExternalID, customers, watchedCustomer]
  );
  const isEDICustomerSelected = isMGBEDICustomerSelected(
    customersByExternalID,
    customers,
    watchedCustomer,
    isCreateMode
  );

  const setVisibleCustomers = useCallback(
    (
      nextCustomers:
        | Maybe<TMSCustomer[]>
        | ((prevCustomers: Maybe<TMSCustomer[]>) => Maybe<TMSCustomer[]>)
    ) => {
      setCustomers((prevCustomers) => {
        const resolvedCustomers =
          typeof nextCustomers === 'function'
            ? nextCustomers(prevCustomers)
            : nextCustomers;
        if (!resolvedCustomers) {
          return resolvedCustomers;
        }
        const nextLookup = mergeMGBCustomersByExternalID(
          customersByExternalIDRef.current,
          resolvedCustomers
        );
        customersByExternalIDRef.current = nextLookup;
        setCustomersByExternalID(nextLookup);

        return isCreateMode
          ? filterMGBCreateCustomers(resolvedCustomers, nextLookup, {
              retainExternalTMSID: retainedCreateCustomerIdRef.current,
            })
          : filterMGBCustomers(resolvedCustomers);
      });
    },
    [isCreateMode]
  );
  const setVisibleCustomersRef = useRef(setVisibleCustomers);
  setVisibleCustomersRef.current = setVisibleCustomers;

  useEffect(() => {
    if (!isCreateMode || !threadId || createSucceeded) {
      return undefined;
    }

    const formStorageKey = `${tmsName}_${threadId}`;
    const savedState = FormStorageService.getFormState<
      NormalizedLoad,
      NormalizedLoad
    >(formStorageKey);

    const savedSuggestionId = savedState?.clickedSuggestion?.id ?? null;
    const suggestionContextOk =
      activeLoadBuildingSuggestionId == null ||
      activeLoadBuildingSuggestionId === savedSuggestionId;

    const shouldRestoreDraft =
      savedState != null &&
      savedState.threadID === threadId &&
      suggestionContextOk &&
      draftRestoredForThreadRef.current !== threadId;

    if (shouldRestoreDraft && savedState) {
      draftRestoredForThreadRef.current = threadId;
      skipFormDefaultsResetRef.current = true;
      draftRestoreSuggestionIdRef.current = savedSuggestionId;

      setShowAIHints(true);

      const suggestionAlreadyActive =
        activeLoadBuildingSuggestionId != null &&
        activeLoadBuildingSuggestionId === savedSuggestionId;

      if (savedSuggestionId != null && !suggestionAlreadyActive) {
        void goToSuggestionRef.current({
          suggestionID: savedSuggestionId,
        });
      }

      const fieldNames = flattenFormValues(
        savedState.values as unknown as Record<string, unknown>
      );

      const savedLbSuggestion =
        savedState.clickedSuggestion?.pipeline ===
        SuggestionPipelines.LoadBuilding
          ? (savedState.clickedSuggestion as LoadBuildingSuggestions)
          : createModeLbSuggestionRef.current;

      let restoreBase: NormalizedLoad;
      if (savedLbSuggestion) {
        restoreBase = buildMGBFormFromSuggestion(
          savedLbSuggestion.suggested as SuggestedLoad,
          tmsName,
          {
            drumkitQuoteNumber: savedLbSuggestion.drumkitQuoteNumber,
            quoteRequestId: savedLbSuggestion.quoteRequestId,
            lowBuyRate: savedLbSuggestion.lowBuyRate,
            highBuyRate: savedLbSuggestion.highBuyRate,
          }
        );
      } else {
        restoreBase = withMGBFormDefaults(
          normalizeLoad(tmsName, createInitLoad())
        );
        fieldNames.forEach((fieldName) => {
          const isDirty: Undef<boolean> = get(
            savedState.dirtyFields,
            fieldName
          );
          if (isDirty === undefined || !isDirty) {
            set(restoreBase, fieldName, get(savedState.values, fieldName));
          }
        });
      }

      formMethods.reset(restoreBase, {
        keepDefaultValues: false,
        keepTouched: false,
        keepDirtyValues: false,
        keepDirty: false,
      });

      fieldNames.forEach((fieldName) => {
        const isDirty: Undef<boolean> = get(savedState.dirtyFields, fieldName);
        if (isDirty) {
          formMethods.setValue(
            fieldName as FieldPath<NormalizedLoad>,
            get(savedState.values, fieldName),
            { shouldDirty: true, shouldTouch: true }
          );
        }
      });

      const restoredCustomer = savedState.values.customer;
      const customerID = restoredCustomer?.externalTMSID?.trim() ?? '';
      if (customerID && restoredCustomer) {
        setVisibleCustomersRef.current((prevCustomers) =>
          injectSelectedObject(restoredCustomer, prevCustomers ?? [])
        );
      }
      prevCustomerExternalTMSIDRef.current = customerID || undefined;
      alignEquipmentScopedCustomerId(customerID);

      lastFormDefaultsSnapshotRef.current = JSON.stringify(
        formMethods.getValues()
      );

      if (savedSuggestionId == null || suggestionAlreadyActive) {
        skipFormDefaultsResetRef.current = false;
        draftRestoreSuggestionIdRef.current = undefined;
        lastFormDefaultsSnapshotRef.current = JSON.stringify(
          defaultValuesRef.current
        );
      }
    }

    if (!persistCreateDraftsRef.current) {
      return undefined;
    }

    const resolveSuggestionForStorage = () => {
      const live = createModeLbSuggestionRef.current;
      if (live) {
        return live;
      }
      return (
        FormStorageService.getFormState<NormalizedLoad, NormalizedLoad>(
          formStorageKey
        )?.clickedSuggestion ?? null
      );
    };

    const skipInitialSaveForSuggestionMismatch =
      savedState != null &&
      savedState.threadID === threadId &&
      !suggestionContextOk;

    if (
      !skipInitialSaveForSuggestionMismatch &&
      (!savedState || threadId === savedState.threadID)
    ) {
      FormStorageService.saveFormState(formStorageKey, {
        threadID: threadId,
        clickedSuggestion: resolveSuggestionForStorage(),
        values: formMethods.getValues(),
        dirtyFields: formMethods.formState.dirtyFields,
      });
    }

    const subscription = formMethods.watch((value) => {
      if (!persistCreateDraftsRef.current) {
        return;
      }
      if (!savedState || threadId === savedState.threadID) {
        FormStorageService.saveFormState(formStorageKey, {
          threadID: threadId,
          values: value as NormalizedLoad,
          clickedSuggestion: resolveSuggestionForStorage(),
          dirtyFields: formMethods.formState.dirtyFields,
        });
      }
    });

    return () => {
      subscription.unsubscribe();
    };
  }, [
    isCreateMode,
    threadId,
    createSucceeded,
    activeLoadBuildingSuggestionId,
    tmsName,
    formMethods,
  ]);

  const customerId = watchedCustomerExternalTMSID?.trim() ?? '';

  const customerNameForOptions = useMemo(
    () =>
      resolveMGBCustomerNameForOptions(
        customerId,
        selectedCustomer?.name,
        watchedCustomerName
      ),
    [customerId, selectedCustomer?.name, watchedCustomerName]
  );

  const {
    equipmentTypes,
    isLoading: isEquipmentLoading,
    refresh: refreshEquipmentTypes,
  } = useFetchEquipmentTypes(tmsID, { customerName: customerNameForOptions });

  useEffect(() => {
    if (createModeLoadBuildingSuggestionId == null || createSucceeded) {
      pendingEquipmentSettledRevalidationSuggestionIdRef.current = null;
      equipmentSettledRevalidationSuggestionIdRef.current = undefined;

      return undefined;
    }

    if (skipFormDefaultsResetRef.current) {
      pendingEquipmentSettledRevalidationSuggestionIdRef.current = null;
      equipmentSettledRevalidationSuggestionIdRef.current =
        createModeLoadBuildingSuggestionId;

      return undefined;
    }

    if (isEquipmentLoading) {
      pendingEquipmentSettledRevalidationSuggestionIdRef.current =
        createModeLoadBuildingSuggestionId;

      return undefined;
    }

    if (
      pendingEquipmentSettledRevalidationSuggestionIdRef.current !==
      createModeLoadBuildingSuggestionId
    ) {
      return undefined;
    }

    if (
      equipmentSettledRevalidationSuggestionIdRef.current ===
      createModeLoadBuildingSuggestionId
    ) {
      pendingEquipmentSettledRevalidationSuggestionIdRef.current = null;
      return undefined;
    }

    const suggestionId = createModeLoadBuildingSuggestionId;

    return scheduleLoadBuildingSuggestionValidation(true, () => {
      equipmentSettledRevalidationSuggestionIdRef.current = suggestionId;
      pendingEquipmentSettledRevalidationSuggestionIdRef.current = null;
      void runSuggestionHydrationValidationRef.current();
    });
  }, [isEquipmentLoading, createModeLoadBuildingSuggestionId, createSucceeded]);

  const {
    serviceTypes,
    isLoading: isServiceTypesLoading,
    refresh: refreshServiceTypes,
  } = useFetchServiceTypes(tmsID, { customerName: customerNameForOptions });
  const {
    charges: accessorialChargeSuggestions,
    isLoading: isAccessorialChargesLoading,
    refresh: refreshAccessorialCharges,
  } = useFetchMGBAccessorialCharges(tmsID, {
    customerName: customerNameForOptions,
    quoteMode: QuoteMode.TL,
  });
  const {
    specialReferences: customerRepOptions,
    isLoading: isCustomerRepsLoading,
  } = useFetchSpecialReferences(tmsID, {
    customerName: customerNameForOptions,
    name: MGB_CUSTOMER_REP_CODE_TABLE,
  });
  const {
    specialReferences: carrierRepOptions,
    isLoading: isCarrierRepsLoading,
  } = useFetchSpecialReferences(tmsID, {
    customerName: customerNameForOptions,
    name: MGB_CARRIER_REP_CODE_TABLE,
  });
  const {
    specialReferences: trailerLengthOptions,
    isLoading: isTrailerLengthsLoading,
  } = useFetchSpecialReferences(tmsID, {
    customerName: customerNameForOptions,
    name: MGB_TRAILER_LENGTH_CODE_TABLE,
  });
  const isSpecialReferencesLoading =
    isCustomerRepsLoading || isCarrierRepsLoading || isTrailerLengthsLoading;

  // Clear customer-scoped specs and Customer Rep when the selected customer id
  // changes. equipmentScopedCustomerId is realigned wherever we call reset(),
  // so a load switch or defaults refresh never looks like a user switch here.
  useEffect(() => {
    const nextId = watchedCustomerExternalTMSID?.trim() ?? '';
    if (equipmentScopedCustomerId === null) {
      const stamped = equipmentScopedCustomerIdRef.current;
      const initialId = stamped !== null ? stamped : nextId;
      alignEquipmentScopedCustomerId(initialId);

      return;
    }
    if (equipmentScopedCustomerId === nextId) {
      return;
    }

    const prevId = equipmentScopedCustomerId;
    alignEquipmentScopedCustomerId(nextId);

    // First pick ('' -> customer): fields were already empty / not customer-scoped.
    if (!prevId && nextId) {
      clearErrors([
        'specifications.transportType',
        'specifications.transportSize',
      ]);

      return;
    }

    setCustomerRepClearToken((token) => token + 1);
    setValue('specifications.transportType', '', {
      shouldDirty: true,
      shouldValidate: false,
    });
    setValue('specifications.transportSize', '', {
      shouldDirty: true,
      shouldValidate: false,
    });
    setValue('accessorials', [], {
      shouldDirty: true,
      shouldValidate: false,
    });
    const lineItems = getValues('rateData.customerLineItems') ?? [];
    setValue(
      'rateData.customerLineItems',
      lineItems.filter((item) => item.label === 'Hazardous Material'),
      { shouldDirty: true, shouldValidate: false }
    );
    clearErrors([
      'specifications.transportType',
      'specifications.transportSize',
    ]);
  }, [
    watchedCustomerExternalTMSID,
    equipmentScopedCustomerId,
    setValue,
    getValues,
    clearErrors,
  ]);

  const searchCustomersNonDestructive = useCallback(
    async (field: 'name' | 'externalTMSID', value: string) => {
      if (!tmsID) return [];
      const res = await searchCustomers(tmsID, field, value);

      return res.isOk() ? (res.value.customerList ?? []) : [];
    },
    [tmsID]
  );

  useEffect(() => {
    if (!tmsID) {
      return;
    }
    let cancelled = false;
    setIsCustomersLoading(true);
    getCustomers(tmsID).then((res) => {
      if (cancelled) {
        return;
      }
      if (res.isOk()) {
        setVisibleCustomers(res.value.customerList ?? []);
      } else {
        setVisibleCustomers([]);
        toast({
          description:
            res.error.message || 'Error while fetching customer list.',
          variant: 'destructive',
        });
      }
      setIsCustomersLoading(false);
    });

    return () => {
      cancelled = true;
    };
  }, [setVisibleCustomers, tmsID, toast]);

  // Inject customer from form values so the select resolves on get-load
  // (IDs are often outside the initial getCustomers page). Bill-to is handled
  // separately so it does not pollute the customer dropdown options.
  useEffect(() => {
    if (isCustomersLoading) {
      return;
    }

    const customerId = watchedCustomer?.externalTMSID?.trim();
    if (!customerId) {
      return;
    }

    // Only inject when the customer is missing from the list. Re-injecting on every
    // externalTMSID change would merge a stale customer.name into search results.
    const formName = watchedCustomer?.name?.trim() ?? '';
    if (!formName || formName === customerId) {
      return;
    }

    setVisibleCustomers((prevCustomers) => {
      const list = prevCustomers ?? [];
      if (list.some((c) => c.externalTMSID === customerId)) {
        return list;
      }
      return injectCompanyIfMissing(list, watchedCustomer);
    });
  }, [
    isCustomersLoading,
    watchedCustomer,
    watchedCustomer?.externalTMSID,
    setVisibleCustomers,
  ]);

  useEffect(() => {
    if (!isCreateMode) {
      return;
    }
    setCustomers((prevCustomers) => {
      if (!prevCustomers?.length) {
        return prevCustomers;
      }
      const next = filterMGBCreateCustomers(
        prevCustomers,
        customersByExternalIDRef.current,
        {
          retainExternalTMSID: retainedCreateCustomerIdRef.current,
        }
      );
      if (
        next.length === prevCustomers.length &&
        next.every((c, i) => c === prevCustomers[i])
      ) {
        return prevCustomers;
      }
      return next;
    });
  }, [isCreateMode, watchedCustomerExternalTMSID]);

  useMGBCustomerHydration({
    tmsID,
    isCreateMode,
    isCustomersLoading,
    customerExternalTMSID: watchedCustomerExternalTMSID,
    customers,
    customersByExternalID,
    setVisibleCustomers,
    setValue,
    getValues,
  });

  const handleClearForm = () => {
    setShowAIHints(false);
    persistCreateDraftsRef.current = false;
    skipFormDefaultsResetRef.current = false;
    draftRestoreSuggestionIdRef.current = undefined;
    pendingEquipmentSettledRevalidationSuggestionIdRef.current = null;
    equipmentSettledRevalidationSuggestionIdRef.current = undefined;

    if (threadId) {
      draftRestoredForThreadRef.current = threadId;
    }

    createModeLbSuggestionRef.current = null;
    FormStorageService.clearFormState(`${tmsName}_${threadId}`);
    formMethods.reset(initMGBLoadForm(tmsName), {
      keepDefaultValues: false,
      keepDirty: false,
      keepTouched: false,
    });
    setCreateSucceeded(false);
    setCreatedLoad(null);
    setShowDuplicateWarningDialog(false);
    setPendingSubmissionData(undefined);
    setWarningMessage(undefined);
    createSessionSuggestionRef.current = null;
    prevCustomerExternalTMSIDRef.current = undefined;
    alignEquipmentScopedCustomerId('');
    lastFormDefaultsSnapshotRef.current = null;
    setCurrentState((prevState) => ({
      ...prevState,
      clickedSuggestion: null,
    }));

    persistCreateDraftsRef.current = true;
  };

  const createdLoadDisplayId = resolveCreatedLoadDisplayId(createdLoad);
  const createdLoadDisplayLabel = `${resolveCreatedLoadDisplayLabel(createdLoad)}:`;
  const showEditActions = !isCreateMode && !loadIsCancelled;

  const handleConfirmCancelLoad = async () => {
    const loadId = load?.ID;
    if (loadId == null) {
      toast({
        description: 'Missing load ID for cancel.',
        variant: 'destructive',
      });

      return;
    }

    setIsCancellingLoad(true);
    try {
      const res = await cancelLoad(loadId);
      if (res.isOk()) {
        const { load: cancelledLoad, message } = res.value;
        setLoadIsCancelled(true);
        setIsCancelDialogOpen(false);
        const updatedDefaults = withMGBFormDefaults(
          normalizeLoad(tmsName, cancelledLoad)
        );
        lastFormDefaultsSnapshotRef.current = JSON.stringify(updatedDefaults);
        alignEquipmentScopedCustomerId(
          updatedDefaults.customer?.externalTMSID?.trim() ?? ''
        );
        reset(updatedDefaults, {
          keepDefaultValues: false,
          keepDirty: false,
          keepTouched: false,
        });
        toast({
          title: message || 'Load cancelled successfully',
          variant: 'success',
        });
        onSuccess?.({ load: cancelledLoad, message });
      } else {
        const message = res.error.message || 'Unable to cancel load.';
        toast({
          description: message,
          variant: 'destructive',
        });
        onError?.({ load: res.error.load, message });
      }
    } finally {
      setIsCancellingLoad(false);
    }
  };

  const processFormSubmit = async (
    values: NormalizedLoad,
    options?: { bypassDuplicateCheck?: boolean }
  ) => {
    const { bypassDuplicateCheck = false } = options ?? {};

    if (submitInFlightRef.current) {
      return;
    }

    if (!isCreateMode && loadIsCancelled) {
      return;
    }

    if (!Number.isFinite(tmsID) || tmsID <= 0) {
      toast({
        description: 'Missing MercuryGate Boomi TMS id.',
        variant: 'destructive',
      });

      return;
    }

    submitInFlightRef.current = true;
    setIsSubmitting(true);
    setWarningMessage(undefined);

    try {
      let customerForCreate = selectedCustomer ?? values.customer;
      let customersForCreate = customers;

      if (isCreateMode) {
        let ediBlocked = isEDICustomerSelected;
        const customerId = values.customer?.externalTMSID?.trim();
        if (
          !ediBlocked &&
          customerId &&
          typeof resolveMGBSelectedCustomer(
            customersByExternalID,
            customers,
            values.customer
          )?.ediEnabled !== 'boolean'
        ) {
          const searchedCustomers = await searchCustomersNonDestructive(
            'externalTMSID',
            customerId
          );
          const exactMatch = searchedCustomers.find(
            (customer) => customer.externalTMSID === customerId
          );
          if (exactMatch) {
            setVisibleCustomers((prevCustomers) =>
              upsertTMSCustomer(prevCustomers ?? [], exactMatch)
            );

            customerForCreate = exactMatch;
            customersForCreate = upsertTMSCustomer(
              customersForCreate ?? [],
              exactMatch
            );
            ediBlocked = Boolean(exactMatch.ediEnabled);
          }
        }

        if (ediBlocked) {
          submitManualPathsRef.current = ['customer.externalTMSID'];
          applyManualValidationErrors(
            formMethods,
            [
              {
                path: 'customer.externalTMSID',
                message: MGB_EDI_CUSTOMER_BLOCK_MESSAGE,
              },
            ],
            loadBuildingManualValidationDisplayOptions({
              tmsName: TMS.MercurygateBoomi,
              activeTabs,
              setActiveTabs,
              toast,
              afterErrorHandled: () => undefined,
            })
          );

          return;
        }
      }

      const updateLoadId = !isCreateMode ? load?.ID : undefined;
      if (!isCreateMode && (updateLoadId == null || !load)) {
        toast({
          description: 'Missing load ID for update.',
          variant: 'destructive',
        });

        return;
      }

      let createServiceIDForRequest: number | undefined;
      if (isCreateMode) {
        if (!Number.isFinite(serviceID) || serviceID <= 0) {
          toast({
            description: 'Missing service id from session.',
            variant: 'destructive',
          });

          return;
        }
        createServiceIDForRequest = serviceID;
      }

      // Prefer nested path values — do not write back into useFieldArray here.
      const hydratedCommodities = hydrateMGBCommodityStopFieldsFromForm(
        getValues,
        getValues('commodities') ?? values.commodities
      );

      const formValues: NormalizedLoad = {
        ...values,
        stops: getValues('stops') ?? values.stops,
        commodities: hydratedCommodities,
        additionalReferences: canonicalizeSpecialReferenceCodes(
          getValues('additionalReferences') ?? values.additionalReferences,
          {
            customerRepOptions,
            carrierRepOptions,
            trailerLengthOptions,
          }
        ),
      };

      const commodityErrors = validateMGBCommoditiesForSubmit(
        formValues.commodities
      );
      const referenceErrors = validateMGBAdditionalReferencesForSubmit(
        formValues.additionalReferences
      );
      const stateErrors = validateMGBStopStatesForSubmit(formValues);
      const preparedForWire = prepareMGBValuesForWire(formValues);
      const multiStopErrors = validateMGBMultiStopsForSubmit(preparedForWire);
      const rateErrors = validateMGBRatesForSubmit(formValues.rateData);
      const manualErrors = [
        ...commodityErrors,
        ...referenceErrors,
        ...stateErrors,
        ...multiStopErrors,
        ...rateErrors,
      ];
      if (manualErrors.length > 0) {
        submitManualPathsRef.current = manualErrors.map((error) =>
          String(error.path)
        );
        focusRouteStopFromValidationErrors(manualErrors);
        applyManualValidationErrors(
          formMethods,
          manualErrors,
          loadBuildingManualValidationDisplayOptions({
            tmsName: TMS.MercurygateBoomi,
            activeTabs,
            setActiveTabs,
            toast,
            afterErrorHandled: () => undefined,
          })
        );

        return;
      }

      if (
        isCreateMode &&
        createSucceeded &&
        createdLoad &&
        !bypassDuplicateCheck
      ) {
        setPendingSubmissionData(formValues);
        setShowDuplicateWarningDialog(true);

        return;
      }

      if (isCreateMode && createServiceIDForRequest != null) {
        const createValues = preparedForWire;

        if (
          clickedSuggestion?.pipeline === SuggestionPipelines.LoadBuilding &&
          Number.isFinite(clickedSuggestion.id) &&
          clickedSuggestion.id > 0
        ) {
          createSessionSuggestionRef.current = { id: clickedSuggestion.id };
        }
        const formQuoteNumber = getAdditionalReferenceNumber(
          createValues.additionalReferences,
          MGB_QUOTE_NUMBER_REF_TYPE
        );
        const quoteRequestIdForCreate =
          resolveMGBQuoteRequestIdForCreate(formQuoteNumber);

        const res = await guardedCreateLoad(
          customerForCreate ?? values.customer,
          (opts) =>
            withLoadBuildingSuggestionContext(
              {
                clickedSuggestion: createSessionSuggestionRef.current,
                threadId: threadId ?? '',
                emailId: emailId ?? 0,
                emailExternalId: threadItemId ?? '',
                quoteRequestId: quoteRequestIdForCreate,
              },
              buildCreateLoadRequest(createValues, {
                serviceID,
                tmsID,
                acknowledgeProdTMSOverride: opts.acknowledgeProdTMSOverride,
              })
            ),
          customersForCreate
        );
        if (!res) {
          return;
        }

        const finishCreate = (
          created: Load,
          message: string,
          isWarning: boolean
        ) => {
          persistCreateDraftsRef.current = false;
          FormStorageService.clearFormState(`${tmsName}_${threadId}`);
          setCreateSucceeded(true);
          setCreatedLoad(created);
          setCurrentState((prevState) => {
            if (
              clickedSuggestion &&
              clickedSuggestion.pipeline === SuggestionPipelines.LoadBuilding
            ) {
              return {
                ...prevState,
                ...applyCompleteKitSuggestions(
                  prevState,
                  clickedSuggestion.id,
                  SuggestionPipelines.LoadBuilding
                ),
              };
            }
            return prevState;
          });

          if (isWarning && message) {
            setWarningMessage(message);
            toast({
              title: message,
              description: resolveCreateSuccessDescription(created),
              variant: 'warning',
            });
          }
          const builtQuoteNumber = getAdditionalReferenceNumber(
            createValues.additionalReferences,
            MGB_QUOTE_NUMBER_REF_TYPE
          );
          posthog?.capture(
            builtQuoteNumber
              ? ButtonNamePosthog.MGBLoadBuiltWithQuoteNumber
              : ButtonNamePosthog.MGBLoadBuiltWithoutQuoteNumber,
            {
              drumkitQuoteNumber: builtQuoteNumber || undefined,
              quoteRequestId:
                parseDrumkitQuoteNumber(builtQuoteNumber) || undefined,
            }
          );
          onSuccess?.({ load: created, message });
        };

        if (res.isOk()) {
          const { load: created, message, errorLevel } = res.value;
          finishCreate(created, message ?? '', errorLevel === 'warning');
        } else {
          const errorPayload = res.error;
          const message =
            errorPayload?.message || 'Failed to create MercuryGate load.';
          const isWarning = errorPayload?.level === 'warning';

          if (isWarning && errorPayload?.load) {
            finishCreate(errorPayload.load, message, true);
          } else {
            toast({
              description: message,
              variant: isWarning ? 'warning' : 'destructive',
            });
            onError?.({ load: errorPayload?.load, message });
          }
        }

        return;
      }

      const prepared = applyMGBWallClockDatesForWire(preparedForWire);
      const denormalizedPickup = withResolvedMGBStopCountry(
        prepared.pickup as Load['pickup']
      );
      const denormalizedConsignee = withResolvedMGBStopCountry(
        prepared.consignee as Load['consignee']
      );

      const existingLoad = load!;

      const customer = getValues('customer');
      const billTo = getValues('billTo');
      const wireStops = buildWireStops(prepared);

      const res = await updateTMS(updateLoadId!, {
        operation: TMSOperation.UpdateLoad,
        load: {
          ...existingLoad,
          ...prepared,
          customer,
          billTo,
          freightTrackingID: existingLoad.freightTrackingID,
          pickup: denormalizedPickup,
          consignee: denormalizedConsignee,
          stops: wireStops as unknown as Load['stops'],
          moreThanTwoStops: wireStops.length > 2,
          specifications: mergeMGBSpecificationsForUpdate(
            prepared,
            existingLoad.specifications
          ),
          commodities: buildLoadCommodities(prepared.commodities),
          rateData: {
            ...prepared.rateData,
            ...buildRateData(prepared),
          },
          notes: buildNotes(prepared) as Load['notes'],
          accessorials: buildAccessorials(prepared.accessorials).map((a) => ({
            ...a,
            isFavorite: false,
          })),
          additionalReferences: buildAdditionalReferences(prepared),
        } as Load,
      });

      if (res.isOk()) {
        toast({
          description: res.value.message || 'Load updated successfully',
          variant: 'success',
        });
        await acceptLoadViewSuggestion(
          clickedSuggestion,
          {
            ...existingLoad,
            ...values,
            customer,
            billTo,
            pickup: denormalizedPickup,
            consignee: denormalizedConsignee,
          } as Load,
          setCurrentState,
          undefined,
          tmsName
        );
        const updatedDefaults = withMGBFormDefaults(
          normalizeLoad(tmsName, res.value.load)
        );
        lastFormDefaultsSnapshotRef.current = JSON.stringify(updatedDefaults);
        alignEquipmentScopedCustomerId(
          updatedDefaults.customer?.externalTMSID?.trim() ?? ''
        );
        reset(updatedDefaults, {
          keepDefaultValues: false,
          keepDirty: false,
          keepTouched: false,
        });
        onSuccess?.(res.value);
      } else {
        const message =
          res.error.message || 'Failed to update MercuryGate load.';
        toast({
          description: message,
          variant: 'destructive',
        });
        onError?.(res.error);
      }
    } catch (err) {
      const isCollectionMethodError =
        err instanceof Error &&
        err.message === COLLECTION_METHOD_REQUIRED_ERROR;
      let submitErrorDescription = 'Error updating load.';
      if (isCollectionMethodError) {
        submitErrorDescription = 'Payment method is required.';
      } else if (isCreateMode) {
        submitErrorDescription = 'Error building load.';
      }
      toast({
        description: submitErrorDescription,
        variant: 'destructive',
      });
      onError?.({});
    } finally {
      submitInFlightRef.current = false;
      setIsSubmitting(false);
    }
  };

  const handleSubmit: SubmitHandler<NormalizedLoad> = async (values) => {
    await processFormSubmit(values);
  };

  const onFormSubmit = (event?: BaseSyntheticEvent) => {
    const stalePaths = [
      ...submitManualPathsRef.current,
      ...hydrationManualPathsRef.current,
    ];
    if (stalePaths.length > 0) {
      clearErrors(stalePaths as never);
      submitManualPathsRef.current = [];
    }

    return formMethods.handleSubmit(handleSubmit, handleInvalid)(event);
  };

  const handleConfirmedSubmit = async () => {
    const data = pendingSubmissionData ?? formMethods.getValues();

    try {
      await processFormSubmit(data, { bypassDuplicateCheck: true });
    } finally {
      setShowDuplicateWarningDialog(false);
      setPendingSubmissionData(undefined);
    }
  };

  const handleInvalid: SubmitErrorHandler<NormalizedLoad> = (errors) => {
    focusRouteStopFromValidationErrors(formatFormErrors(errors));
    handleFormErrors(errors, {
      tmsName: TMS.MercurygateBoomi,
      maxLabelsToShow: DEFAULT_MAX_VALIDATION_TOAST_LABELS,
      activeTabs,
      setActiveTabs,
      toast,
    });
  };

  return (
    <div className='mb-5'>
      {isTestingEnvironment ? (
        <div
          role='status'
          className='mt-3 mb-3 rounded border-l-4 border-info-500 bg-info-50 px-3 py-2'
        >
          <Flex align='center' gap='sm'>
            <InfoIcon
              className='h-4 w-4 shrink-0 text-info-600'
              strokeWidth={2}
            />
            <Typography variant='body-xs' className='text-neutral-700'>
              You&apos;re connected to the MercuryGate QA environment.
            </Typography>
          </Flex>
        </div>
      ) : null}
      <LoadContextProvider
        disabled={true}
        fieldAttributes={[]}
        loadAttrsObj={{} as any}
        invalidateLoad={() => Promise.resolve()}
      >
        <ExtendedFormProvider
          aiDefaultValues={showAIHints}
          highlightDirtyFields={!isCreateMode}
        >
          <FormProvider {...formMethods}>
            <form onSubmit={onFormSubmit} className='w-full'>
              <LoadBuildingAccordionControls
                activeTabs={activeTabs}
                allTabs={allTabs}
                sectionTabs={sectionTabs}
                onExpandAll={expandAll}
                onCollapseAll={collapseAll}
                defaultView={defaultView}
                onDefaultViewChange={setDefaultView}
              />
              <Accordion
                type='multiple'
                value={activeTabs}
                onValueChange={handleAccordionValueChange}
                className='w-full'
              >
                <LoadSectionAccordionItem
                  label='Customer'
                  icon={<Building2 className='h-6 w-6' strokeWidth={1} />}
                  name={AvailableTabs.customer}
                  activeTabs={activeTabs}
                >
                  <CustomerSectionForm
                    customers={customers}
                    setCustomers={setVisibleCustomers}
                    isLoadingCustomers={isCustomersLoading}
                    setIsLoadingCustomers={setIsCustomersLoading}
                    tmsID={tmsID}
                    isCreateMode={isCreateMode}
                    customersByExternalID={customersByExternalID}
                    ediBlockedMessage={
                      isEDICustomerSelected
                        ? MGB_EDI_CUSTOMER_BLOCK_MESSAGE
                        : undefined
                    }
                    prevCustomerExternalTMSIDRef={prevCustomerExternalTMSIDRef}
                  />
                </LoadSectionAccordionItem>

                {!isCreateMode ? (
                  <LoadSectionAccordionItem
                    label='Bill To'
                    icon={<ReceiptIcon className='h-6 w-6' strokeWidth={1} />}
                    name={AvailableTabs.billTo}
                    activeTabs={activeTabs}
                  >
                    <BillToSectionForm
                      customers={customers}
                      setCustomers={setCustomers}
                      isLoadingCustomers={isCustomersLoading}
                      setIsLoadingCustomers={setIsCustomersLoading}
                      tmsID={tmsID}
                      prevCustomerExternalTMSIDRef={
                        prevCustomerExternalTMSIDRef
                      }
                    />
                  </LoadSectionAccordionItem>
                ) : null}

                <LoadSectionAccordionItem
                  label='Route'
                  icon={<MapPinnedIcon className='h-6 w-6' strokeWidth={1} />}
                  name={AvailableTabs.route}
                  activeTabs={activeTabs}
                >
                  <RouteStops
                    formMethods={formMethods}
                    activeStopTab={activeRouteStopTab}
                    activeStopTabFocusKey={routeStopFocusKey}
                    onActiveStopTabHandled={handleActiveRouteStopTabHandled}
                    allowAddStops={isCreateMode}
                  />
                </LoadSectionAccordionItem>

                <LoadSectionAccordionItem
                  label='Specs'
                  icon={<PackageIcon className='h-6 w-6' strokeWidth={1} />}
                  name={AvailableTabs.commodities}
                  activeTabs={activeTabs}
                >
                  <SpecificationsForm
                    equipmentTypes={equipmentTypes}
                    isLoading={isEquipmentLoading}
                    suggestionMaps={KENCO_MGB_SUGGESTION_MAPS}
                    customerSelected={Boolean(customerId)}
                    equipmentSelectKey={customerId || 'none'}
                    onRefreshEquipmentTypes={
                      customerId
                        ? () => void refreshEquipmentTypes()
                        : undefined
                    }
                    onJumpToRatesAccessorials={jumpToRatesAccessorials}
                    onUnresolvedEquipmentCleared={
                      expandSpecsForUnresolvedEquipment
                    }
                  />
                </LoadSectionAccordionItem>

                <LoadSectionAccordionItem
                  label='Rates'
                  icon={
                    <CircleDollarSignIcon className='h-6 w-6' strokeWidth={1} />
                  }
                  name={AvailableTabs.rateData}
                  activeTabs={activeTabs}
                >
                  <RatesForm
                    serviceTypes={serviceTypes}
                    isServiceTypesLoading={isServiceTypesLoading}
                    isAccessorialChargesLoading={isAccessorialChargesLoading}
                    customerSelected={Boolean(customerId)}
                    accessorialChargeSuggestions={accessorialChargeSuggestions}
                    onRefreshServiceTypes={
                      customerId
                        ? () => {
                            void refreshServiceTypes();
                            void refreshAccessorialCharges();
                          }
                        : undefined
                    }
                  />
                </LoadSectionAccordionItem>

                <LoadSectionAccordionItem
                  label='References'
                  icon={<TagsIcon className='h-6 w-6' strokeWidth={1} />}
                  name={AvailableTabs.references}
                  activeTabs={activeTabs}
                >
                  <AdditionalReferencesForm
                    // Remount on create-mode LB suggestion switch so local
                    // autofill/override refs cannot outlive parent reset.
                    // Quote association itself lives in RHF defaultValues
                    // (buildMGBFormFromSuggestion) — submit reads form DKQN.
                    key={
                      isCreateMode
                        ? `mgb-refs-${createModeLoadBuildingSuggestionId ?? 'none'}`
                        : 'mgb-refs-view'
                    }
                    referenceTypes={referenceTypes}
                    isLoading={isReferenceTypesLoading}
                    isCreateMode={isCreateMode}
                    customerSelected={Boolean(customerId)}
                    customerRepOptions={customerRepOptions}
                    isCustomerRepsLoading={isCustomerRepsLoading}
                    carrierRepOptions={carrierRepOptions}
                    isCarrierRepsLoading={isCarrierRepsLoading}
                    trailerLengthOptions={trailerLengthOptions}
                    isTrailerLengthsLoading={isTrailerLengthsLoading}
                    customerRepClearToken={customerRepClearToken}
                  />
                </LoadSectionAccordionItem>

                <LoadSectionAccordionItem
                  label='Notes'
                  icon={<StickyNote className='h-6 w-6' strokeWidth={1} />}
                  name={AvailableTabs.notes}
                  activeTabs={activeTabs}
                >
                  <NotesForm />
                </LoadSectionAccordionItem>
              </Accordion>

              <Flex direction='col' className='w-full mt-4'>
                {showEditActions ? (
                  <>
                    <Button
                      buttonNamePosthog={ButtonNamePosthog.UpdateTMS}
                      type='submit'
                      className='w-full'
                      disabled={
                        isSubmitting ||
                        isCancellingLoad ||
                        isCustomersLoading ||
                        isEquipmentLoading ||
                        isServiceTypesLoading ||
                        isSpecialReferencesLoading
                      }
                      logProperties={{ serviceID, tmsID }}
                    >
                      {isSubmitting ? <ButtonLoader /> : ButtonText.UpdateTMS}
                    </Button>

                    <Button
                      buttonNamePosthog={ButtonNamePosthog.CancelMGBLoad}
                      type='button'
                      variant='destructive'
                      className='w-full mt-4 text-md font-medium'
                      disabled={
                        isSubmitting ||
                        isCancellingLoad ||
                        isCustomersLoading ||
                        isEquipmentLoading ||
                        isServiceTypesLoading
                      }
                      onClick={() => setIsCancelDialogOpen(true)}
                      logProperties={{ serviceID, tmsID }}
                    >
                      {ButtonText.CancelLoad}
                    </Button>

                    <MGBCancelLoadDialog
                      open={isCancelDialogOpen}
                      onOpenChange={setIsCancelDialogOpen}
                      onConfirm={handleConfirmCancelLoad}
                      isLoading={isCancellingLoad}
                    />
                  </>
                ) : null}
                {!showEditActions && isCreateMode ? (
                  <Button
                    buttonNamePosthog={ButtonNamePosthog.BuildLoad}
                    type='submit'
                    className='w-full'
                    disabled={
                      isSubmitting ||
                      isEDICustomerSelected ||
                      isCustomersLoading ||
                      isEquipmentLoading ||
                      isServiceTypesLoading ||
                      isSpecialReferencesLoading
                    }
                    logProperties={{ serviceID, tmsID }}
                  >
                    {isSubmitting ? <ButtonLoader /> : ButtonText.BuildLoad}
                  </Button>
                ) : null}

                {isCreateMode &&
                  (formMethods.formState.isDirty || createSucceeded) && (
                    <Flex
                      direction='row'
                      justify='center'
                      align='center'
                      className='w-full'
                    >
                      <Button
                        buttonNamePosthog={ButtonNamePosthog.ClearForm}
                        type='button'
                        className='mt-4 h-8 text-sm text-neutral-700'
                        disabled={isSubmitting}
                        variant='outline'
                        onClick={handleClearForm}
                        logProperties={{ serviceID, tmsID }}
                      >
                        {ButtonText.ClearForm}
                      </Button>
                    </Flex>
                  )}

                {isCreateMode && createSucceeded && createdLoadDisplayId ? (
                  <LoadCreatedMessage
                    builtLoadId={createdLoadDisplayId}
                    builtLoadIdLabel={createdLoadDisplayLabel}
                  />
                ) : null}

                {warningMessage ? (
                  <div className='mt-4 rounded border border-warning-300 bg-warning-100 px-3 py-2'>
                    <Typography variant='body-sm' weight='semibold'>
                      Warning
                    </Typography>
                    <Typography variant='body-sm'>{warningMessage}</Typography>
                  </div>
                ) : null}
              </Flex>
            </form>
          </FormProvider>
        </ExtendedFormProvider>
      </LoadContextProvider>

      <DuplicateLoadWarningDialog
        open={showDuplicateWarningDialog}
        onOpenChange={(open) => {
          setShowDuplicateWarningDialog(open);
          if (!open) {
            if (!isSubmitting) {
              setPendingSubmissionData(undefined);
            }
          }
        }}
        onConfirm={handleConfirmedSubmit}
        isLoading={isSubmitting}
      />
    </div>
  );
}
